import React, { useState, useEffect, useCallback } from "react";
import "./styles.scss";
import Header from "../../components/CustomDocumentation/Header";
import StickyMenu from "../../components/CustomDocumentation/StickyMenu";
import PDFViewer from "../../components/CustomDocumentation/PDFViewer";
import FieldManager from "../../components/CustomDocumentation/FieldManager";
import DragDropHandler from "../../components/CustomDocumentation/DragDropHandler";
import MenuHandler from "../../components/CustomDocumentation/MenuHandler";
import ModalManager from "../../components/CustomDocumentation/ModalManager";
import ZoomHandler from "../../components/CustomDocumentation/ZoomHandler";
import { menuArray } from "../../components/CustomDocumentation/utils/fieldTypes";
import {
  getFieldConfig,
  getFieldSettings,
} from "../../components/CustomDocumentation/utils/fieldConfig";
import Text from "../../assets/images/custom_documentation/Text.svg";
import InputFields from "../../assets/images/custom_documentation/Input Fields.svg";
import TextInput from "../../assets/images/custom_documentation/Text Input.svg";
import TextMultiline from "../../assets/images/custom_documentation/Text multiline.svg";
import Date from "../../assets/images/custom_documentation/Date.svg";
import SelectionFields from "../../assets/images/custom_documentation/Selection Fields.svg";
import Dropdown from "../../assets/images/custom_documentation/Dropdown.svg";
import Checkbox from "../../assets/images/custom_documentation/Checkbox.svg";
import Radio from "../../assets/images/custom_documentation/Radio.svg";
import Initials from "../../assets/images/custom_documentation/Initials.svg";
import Signature from "../../assets/images/custom_documentation/Signature.svg";
import { useParams } from "react-router-dom";
import { getData } from "../../api";

// Enhanced menuArray with icons
const enhancedMenuArray = menuArray.map((item) => {
  const iconMap = {
    Text: Text,
    "Input Fields": InputFields,
    "Selection Fields": SelectionFields,
    Initials: Initials,
    Signature: Signature,
  };

  const childIconMap = {
    "Text input": TextInput,
    "Text mulit line input": TextMultiline,
    Date: Date,
    Dropdown: Dropdown,
    Checkbox: Checkbox,
    Radio: Radio,
  };

  const enhancedItem = { ...item, icon: iconMap[item.name] };

  if (item.child) {
    enhancedItem.child = item.child.map((child) => ({
      ...child,
      icon: childIconMap[child.name],
    }));
  }

  return enhancedItem;
});

// Export enhancedMenuArray for use in other components
export { enhancedMenuArray };

const CustomClientDocument = () => {
  let { id } = useParams();
  const [documentData, setDocumentData] = useState(null);
  const [documentName, setDocumentName] = useState(null);
  const [placedFields, setPlacedFields] = useState([]);
  const [originalPlacedFields, setOriginalPlacedFields] = useState([]);
  const [originalDocumentName, setOriginalDocumentName] = useState(null);
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [modalSettingsObj, SetModalSettingsObj] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [hasFieldSettingsChanges, setHasFieldSettingsChanges] = useState(false);

  // Initialize handlers
  const menuHandler = MenuHandler({
    enhancedMenuArray,
    onFieldTypeSelect: setSelectedFieldType,
  });

  const zoomHandler = ZoomHandler({
    initialScale: 1.0,
    onZoomChange: (newScale) => {
      // Scale will be managed internally by components
    },
    onFieldsConstrain: null, // Will be set after fieldManager is created
  });

  const fieldManager = FieldManager({
    selectedFieldType,
    placedFields,
    setPlacedFields,
    pageNumber,
    scale: zoomHandler.scale, // Use current scale from ZoomHandler
    isPdfLoading,
    onFieldPlaced: setSelectedFieldType,
  });

  // Update zoomHandler with fieldManager reference
  zoomHandler.onFieldsConstrain = fieldManager.constrainFieldsToBoundaries;

  const modalManager = ModalManager({
    enhancedMenuArray,
    modalSettingsObj,
    setModalSettingsObj: SetModalSettingsObj,
    onFieldUpdate: (updatedField) => {
      fieldManager.updateField(updatedField);
      setHasFieldSettingsChanges(true);
    },
  });

  const dragDropHandler = DragDropHandler({
    placedFields,
    setPlacedFields,
    scale: zoomHandler.scale,
    onFieldSettingsClick: modalManager.handleSettingsClick,
    onFieldDelete: modalManager.handleDeleteField,
    onFieldDuplicate: fieldManager.handleDuplicateField,
  });
  const handleRightColumnClick = (e) => {
    fieldManager.handleRightColumnClick(e, zoomHandler.scale);
  };
  const handleDeleteField = (fieldId) => {
    setPlacedFields(placedFields.filter((field) => field.id !== fieldId));
  };
  const handleDuplicateField = fieldManager.handleDuplicateField;
  const handleMouseDown = dragDropHandler.handleMouseDown;
  const handleMouseMove = dragDropHandler.handleMouseMove;
  const handleMouseUp = dragDropHandler.handleMouseUp;
  const handleSettingsClick = modalManager.handleSettingsClick;
  const handleZoomIn = zoomHandler.handleZoomIn;
  const handleZoomOut = zoomHandler.handleZoomOut;

  const handleFieldChange = fieldManager.handleFieldChange;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "+" || e.key === "-" || e.key === "0" || e.key === "=")
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (e.key === "+" || e.key === "=") {
          zoomHandler.handleZoomIn(e);
        } else if (e.key === "-") {
          zoomHandler.handleZoomOut(e);
        } else if (e.key === "0") {
          zoomHandler.handleZoomReset();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [zoomHandler]);

  const handleClearAllFields = () => {
    setPlacedFields([]);
  };

  // Function to check if there are unsaved changes
  const hasUnsavedChanges = () => {
    // Check if document name has changed
    if (documentName !== originalDocumentName) {
      return true;
    }

    // Check if field settings have been modified but not saved
    if (hasFieldSettingsChanges) {
      return true;
    }

    // Check if fields have changed
    if (placedFields.length !== originalPlacedFields.length) {
      return true;
    }

    // Deep comparison of fields
    const currentFieldsStr = JSON.stringify(
      placedFields.map((field) => ({
        id: field.id,
        type: field.type,
        x: field.x,
        y: field.y,
        pageNumber: field.pageNumber,
        value: field.value,
        placeholder: field.placeholder,
        width: field.width,
        height: field.height,
        fontSize: field.fontSize,
        needed: field.needed,
        options: field.options,
        multiple_options: field.multiple_options,
        radioGroup: field.radioGroup,
      })),
    );

    const originalFieldsStr = JSON.stringify(
      originalPlacedFields.map((field) => ({
        id: field.id,
        type: field.type,
        x: field.x,
        y: field.y,
        pageNumber: field.pageNumber,
        value: field.value,
        placeholder: field.placeholder,
        width: field.width,
        height: field.height,
        fontSize: field.fontSize,
        needed: field.needed,
        options: field.options,
        multiple_options: field.multiple_options,
        radioGroup: field.radioGroup,
      })),
    );

    return currentFieldsStr !== originalFieldsStr;
  };

  const handlePreviewClose = (updatedFields) => {
    // Apply the same logic as text fields - set placeholder to newly filled value and clear the actual value
    const processedFields = updatedFields.map((field) =>
      (field.type === "initials" || field.type === "Initials") &&
      field.value &&
      field.value.trim() !== ""
        ? { ...field, value: "", placeholder: field.value }
        : field,
    );
    setPlacedFields(processedFields);
  };

  const handleProvideESignature = modalManager.handleProvideESignature;

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      setIsPdfLoading(false);
    }, 100);
  }

  function onDocumentLoadError(error) {
    console.error("Error loading PDF:", error);
    setIsPdfLoading(false);
    return null;
  }

  const parseSettingsData = useCallback((settingsString) => {
    try {
      const settings = JSON.parse(settingsString);
      const fields = [];

      Object.keys(settings).forEach((pageNum) => {
        const pageFields = settings[pageNum];

        Object.keys(pageFields).forEach((fieldId) => {
          const fieldData = pageFields[fieldId];

          const field = {
            id: fieldId,
            pageNumber: parseInt(pageNum),
            type: fieldData.type,
            value: fieldData.type === "signature" ? fieldData.value || "" : "", // For signature fields, preserve the value (contains JSON with signerType and signature data)
            placeholder: fieldData.place_holder || "",
            x: fieldData.left || 0,
            y: fieldData.top || 0,
            width: fieldData.width || 100,
            height: fieldData.height || 30,
            fontSize: fieldData.font_size,
            fontStyle: fieldData.font_style || "Lato",
            characterLimit: fieldData.character_limit,
            scale: fieldData.scale || 1,
            owner: fieldData.owner || "admin",
            needed: fieldData.needed || false,
            options: fieldData.options || [],
            multiple_options: fieldData.multiple_options || false,
            radio_group: fieldData.radio_group || "", // Add radio_group from API data
          };

          const fieldTypeMapping = {
            text: "Text",
            textarea: "Text mulit line input",
            date: "Date",
            select: "Dropdown",
            checkbox: "Checkbox",
            radio: "Radio",
            initials: "Initials",
            signature: "Signature",
          };

          const mappedType = fieldTypeMapping[fieldData.type] || fieldData.type;

          field.type = mappedType;
          field.inputType = fieldData.type;

          fields.push(field);
        });
      });

      return fields;
    } catch (error) {
      console.error("Error parsing settings:", error);
      return [];
    }
  }, []);

  const getDocumentById = useCallback(
    async (id) => {
      const res = await getData(`custom-client-document/${id}`);
      if (res.status === true) {
        setDocumentData(res.data);
        setDocumentName(res.data.name);
        setOriginalDocumentName(res.data.name);
        setIsPdfLoading(true);

        if (res.data.settings) {
          const fields = parseSettingsData(res.data.settings);
          setPlacedFields(fields);
          setOriginalPlacedFields(JSON.parse(JSON.stringify(fields))); // Deep copy
        } else {
          setPlacedFields([]);
          setOriginalPlacedFields([]);
        }
      }
    },
    [parseSettingsData],
  );

  useEffect(() => {
    if (id !== undefined) {
      getDocumentById(id);
    } else {
      setPlacedFields([]);
      setOriginalPlacedFields([]);
    }
  }, [id, getDocumentById]);

  return (
    <div className="custom-client-document">
      <Header
        onClearFields={handleClearAllFields}
        hasFields={placedFields.length > 0}
        documentData={documentData}
        documentName={documentName}
        setDocumentName={setDocumentName}
        placedFields={placedFields}
        canvasSettings={{ scale: zoomHandler.scale }}
        modalSettingsObj={modalSettingsObj}
        hasUnsavedChanges={hasUnsavedChanges}
        onSaveSuccess={() => {
          setOriginalDocumentName(documentName);
          setOriginalPlacedFields(JSON.parse(JSON.stringify(placedFields)));
          setHasFieldSettingsChanges(false);
        }}
        onPreviewClose={handlePreviewClose}
        forceRedirectToClientsDocuments={true}
      />
      <div className="custom-client-document-content">
        <div className="left-column-wrapper">
          <StickyMenu
            enhancedMenuArray={enhancedMenuArray}
            activeMenuIndex={menuHandler.activeMenuIndex}
            activeSubMenuIndex={menuHandler.activeSubMenuIndex}
            dropdownVisible={menuHandler.dropdownVisible}
            pageNumber={pageNumber}
            numPages={numPages}
            scale={zoomHandler.scale}
            handleMenuClick={menuHandler.handleMenuClick}
            setPageNumber={setPageNumber}
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
          />
        </div>
        <div
          className="right-column-wrapper"
          onClick={handleRightColumnClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <PDFViewer
            documentData={documentData}
            pageNumber={pageNumber}
            scale={zoomHandler.scale}
            placedFields={placedFields}
            modalSettingsObj={modalSettingsObj}
            getFieldConfig={getFieldConfig}
            getFieldSettings={getFieldSettings}
            handleMouseDown={handleMouseDown}
            handleDuplicateField={handleDuplicateField}
            handleSettingsClick={handleSettingsClick}
            handleDeleteField={modalManager.handleDeleteField}
            handleFieldChange={handleFieldChange}
            handleProvideESignature={handleProvideESignature}
            setPlacedFields={setPlacedFields}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
            onDocumentLoadError={onDocumentLoadError}
            isPdfLoading={isPdfLoading}
          />
        </div>
      </div>
      {modalManager.renderModals(handleDeleteField)}
    </div>
  );
};

export default CustomClientDocument;
